import useRetrieveStepExecutionLogs, {
  LogsType,
} from '@/hooks/api/stepExecutions/useRetrieveStepExecutionLogs.ts';
import useRetrieveStepRunLogPresentation from '@/hooks/api/stepExecutions/useRetrieveStepRunLogPresentation.ts';
import StepExecutionActivitySection from '@/pages/Run/components/StepExecutionLogs/StepExecutionActivitySection.tsx';
import { getRenderableStepExecutionLogs } from '@/pages/Run/components/StepExecutionLogs/renderableLogs.ts';
import StepRunDataflowSection from '@/pages/Run/components/StepExecutionLogs/StepRunDataflowSection.tsx';
import { getConditionalActivityFallback } from '@/pages/Run/components/StepExecutionLogs/conditionalActivityFallback.ts';
import { Divider, Stack, Text } from '@mantine/core';
import React from 'react';
import {
  LogSeverity,
  StepExecutionLogs as StepExecutionLogsType,
  StepRunDataflowInput,
  StepRunDataflowOutput,
} from '@/types/stepExecutions.ts';
import { ExecutionStatus } from '@/types/statuses.ts';
import { TerminalReasonPresentation } from '@/utils/terminalReasonPresentation.ts';

type props = {
  logItemId?: string;
  stepRunId?: string;
  enabled: boolean;
  logsType?: LogsType;
  stepStatus?: ExecutionStatus | null;
  terminalReasonPresentation?: TerminalReasonPresentation;
  withDivider?: boolean;
  // entityId?: string; // Optional for extract entity errors
};

const getEmptyLogMessage = (stepStatus?: ExecutionStatus | null, loadingLogs = false) => {
  if (loadingLogs) {
    return 'Loading logs...';
  }

  if (stepStatus === ExecutionStatus.Pending) {
    return 'Waiting to start...';
  }

  if (
    stepStatus === ExecutionStatus.Active ||
    stepStatus === ExecutionStatus.ActiveWithErrors ||
    stepStatus === ExecutionStatus.Rerunning ||
    stepStatus === ExecutionStatus.RerunPreparing ||
    stepStatus === ExecutionStatus.Preprocessing
  ) {
    return 'Running...';
  }

  if (stepStatus) {
    return 'No log entries recorded';
  }

  return 'Loading execution state...';
};

const makeEmptyLog = (
  stepStatus?: ExecutionStatus | null,
  loadingLogs = false,
): StepExecutionLogsType => [
  {
    id: '',
    is_internal: false,
    created_at: '',
    message: getEmptyLogMessage(stepStatus, loadingLogs),
    detail_message: '',
    severity: LogSeverity.Debug,
    event_kind: null,
    dataset: null,
    extract_entity_prefix: null,
    extract_entity_id: null,
  },
];

const getInputRoleLabel = (input: StepRunDataflowInput): string | null => {
  const dataInput = input.data_input;
  return (
    [dataInput?.name, dataInput?.last_known_display_name, dataInput?.binding_name].find(
      (label) => label && label !== input.dataset.name,
    ) ?? null
  );
};

const getOutputRoleLabel = (output: StepRunDataflowOutput): string | null =>
  output.output_id || 'Default output';

const StepExecutionLogs = ({
  logItemId,
  stepRunId,
  enabled,
  logsType = 'default',
  stepStatus,
  terminalReasonPresentation,
  withDivider = true,
}: props) => {
  const isExtractEntityErrors = logsType === 'extract_entity_errors';
  const { data: logs, isPending: isLogsPending } = useRetrieveStepExecutionLogs(
    logItemId,
    enabled && isExtractEntityErrors,
    logsType,
  );
  const {
    data: presentation,
    isPending: isPresentationPending,
    isError: isPresentationError,
  } = useRetrieveStepRunLogPresentation(stepRunId, enabled && !isExtractEntityErrors);

  if (isExtractEntityErrors && (!logs || logs.length === 0)) {
    return <></>; // Return null if logs are not available for child logs or extract errors
  }

  const loadingLogs = isExtractEntityErrors
    ? !!logItemId && enabled && isLogsPending
    : !!stepRunId && enabled && isPresentationPending;
  const inputBindings = isExtractEntityErrors ? null : (presentation?.inputs ?? null);
  const outputBindings = isExtractEntityErrors ? null : (presentation?.outputs ?? null);
  const conditionalRoutes = isExtractEntityErrors ? [] : (presentation?.conditional_routes ?? []);
  const hasGroupedDataflow = (inputBindings?.length ?? 0) > 0 || (outputBindings?.length ?? 0) > 0;
  const presentationUnavailable = isPresentationError && !presentation;
  const recordedActivity = getRenderableStepExecutionLogs(presentation?.activity ?? []);
  // Keep stored Activity authoritative. Only fill a missing narrative from
  // structured evidence; never repeat decisions beside their original logs.
  const presentedActivity =
    recordedActivity.length > 0
      ? recordedActivity
      : getConditionalActivityFallback(conditionalRoutes);
  const presentationHasRecordedEntries =
    !!presentation &&
    (presentation.inputs.length > 0 ||
      presentedActivity.length > 0 ||
      presentation.outputs.length > 0);
  const activityLogs = isExtractEntityErrors
    ? (logs ?? makeEmptyLog(stepStatus, loadingLogs))
    : presentationHasRecordedEntries
      ? presentedActivity
      : makeEmptyLog(stepStatus, loadingLogs);
  const hasActivityLogs = getRenderableStepExecutionLogs(activityLogs).length > 0;
  const sectionScopeKey = stepRunId ?? logItemId ?? 'unknown-step-run';
  const wasBlockedUpstream =
    terminalReasonPresentation?.isBlockedByExternalUpstream ||
    terminalReasonPresentation?.isProtectedScopeSkippedUpstream;
  const outcome = wasBlockedUpstream
    ? 'Step did not run'
    : stepStatus === ExecutionStatus.Errors
      ? 'Step failed'
      : stepStatus === ExecutionStatus.PartiallyCompleted
        ? 'Step completed with issues'
        : stepStatus === ExecutionStatus.Cancelled
          ? 'Step cancelled'
          : null;

  return (
    <Stack style={{ maxWidth: '100%', minWidth: 0, overflow: 'hidden' }}>
      {/*{logsType === 'child' && (*/}
      {/*  <Text mr={'2px'} size="sm" fw={500}>*/}
      {/*    Child Error Logs*/}
      {/*  </Text>*/}
      {/*)}*/}
      {/*{logsType === 'extract_errors' && (*/}
      {/*  <Text mr={'2px'} size="sm" fw={500}>*/}
      {/*    Extract Error Logs*/}
      {/*  </Text>*/}
      {/*)}*/}
      {isExtractEntityErrors && (
        <Text mr={'2px'} size="sm" fw={500}>
          Error Logs
        </Text>
      )}
      {!isExtractEntityErrors && isPresentationError && (
        <Text size="sm" c="red">
          {presentationUnavailable
            ? 'Unable to load execution details.'
            : 'Unable to refresh execution details.'}
        </Text>
      )}
      {inputBindings && (
        <StepRunDataflowSection
          key={`${sectionScopeKey}-inputs`}
          title="Inputs"
          bindings={inputBindings}
          getRoleLabel={getInputRoleLabel}
        />
      )}
      {!isExtractEntityErrors && outcome && (
        <Text
          role="status"
          size="sm"
          fw={600}
          c={stepStatus === ExecutionStatus.Errors && !wasBlockedUpstream ? 'red.8' : 'gray.8'}
        >
          {outcome}
        </Text>
      )}
      {!presentationUnavailable && hasActivityLogs && (
        <StepExecutionActivitySection
          key={`${sectionScopeKey}-activity`}
          logs={activityLogs}
          isPending={loadingLogs}
          showTitle={hasGroupedDataflow || conditionalRoutes.length > 0}
        />
      )}
      {outputBindings && (
        <StepRunDataflowSection
          key={`${sectionScopeKey}-outputs`}
          title="Outputs"
          bindings={outputBindings}
          getRoleLabel={getOutputRoleLabel}
        />
      )}
      {withDivider && <Divider />}
    </Stack>
  );
};

export default StepExecutionLogs;
